import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import Layout from 'components/Layout'
import ScreenShell from 'components/ScreenShell/ScreenShell'
import Card from 'components/Card'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import CommunityPostsFeed from './contentPages/CommunityPostsFeed'
import { Users } from 'lucide-react'
import usePartnerProfileDialog from 'features/Partners/hooks/usePartnerProfileDialog'
import { initializeMyPartners } from 'slices/myPartnersSlice'
import { initializeAllGroups, initializeMyGroups } from 'slices/groupSlice'
import { initializePosts } from 'slices/postSlice'
import { patchUser } from 'slices/authSlice'

const Community_Page = () => {
	const dispatch = useDispatch()

	const { openPartner, partnerDialog } = usePartnerProfileDialog()
	const [allGroups, setAllGroups] = useState(false)

	const userLogin = useSelector((state: any) => state.userLogin)
	const { userInfo: user } = userLogin
	const userId = user._id

	const { entities: myGroups, ids: myGroupIds, isInitialized: myGroupsInitialized } = useSelector(
		(state: any) => state.groups,
	)
	const myGroupList = myGroupIds.map(id => myGroups[id])

	const {
		entities: myPartners,
		ids: myPartnerIds,
		isInitialized: myPartnersInitialized,
	} = useSelector((state: any) => state.myPartners)
	const myPartnersList = myPartnerIds.map(id => myPartners[id])

	useEffect(() => {
		if (!myGroupsInitialized) {
			dispatch(initializeMyGroups())
		}

		if (!allGroups) {
			dispatch(initializeMyGroups())
		} else {
			dispatch(initializeAllGroups())
		}

		if (!myPartnersInitialized) {
			dispatch(initializeMyPartners())
		}
	}, [dispatch, allGroups, myPartnersInitialized])

	const handelGroupSelect = async group => {
		const groupId = group._id
		dispatch(initializePosts({ groupId }))
		dispatch(patchUser({ userid: userId, updates: { lastGroup: groupId } }))
	}


	return (
		<ScreenShell>
		<Layout>
			<Layout.Page>
				<Layout.Sidebar>
					<Layout.SidebarUserCard />
					<Card className="border-t-4 border-[#25a4fc] h-[480px]">
						<div className="flex items-center justify-between mb-4">
							<h2 className="flex items-center gap-2 text-[22px] font-semibold m-0">Groups</h2>
							<div className="inline-flex rounded-md shadow-sm">
								<button
									type="button"
									className={`px-3 py-1 text-sm font-medium rounded-l-md border border-blue-500 ${
										!allGroups
											? 'bg-blue-500 text-white'
											: 'bg-white text-blue-500 hover:bg-blue-50'
									}`}
									onClick={() => setAllGroups(false)}
								>
									Mine
								</button>
								<button
									type="button"
									className={`px-3 py-1 text-sm font-medium rounded-r-md border border-blue-500 border-l-0 ${
										allGroups
											? 'bg-blue-500 text-white'
											: 'bg-white text-blue-500 hover:bg-blue-50'
									}`}
									onClick={() => setAllGroups(true)}
								>
									All
								</button>
							</div>
						</div>
						<div className="h-full max-h-[390px] overflow-y-auto">
							{myGroupList && myGroupList.length > 0 && (
								<ul className="list-none p-0 m-0">
									{myGroupList.map((group, index) => (
										<li key={index}>
											<button
												type="button"
												className="flex items-center gap-3 w-full text-left px-2 py-1.5 hover:bg-gray-50 rounded transition-colors cursor-pointer"
												onClick={() => handelGroupSelect(group)}
											>
												<p className="m-0 text-sm font-medium truncate">{group.groupName}</p>
											</button>
										</li>
									))}
								</ul>
							)}
						</div>
					</Card>
				</Layout.Sidebar>
				<Layout.Body>{myGroupsInitialized && <CommunityPostsFeed />}</Layout.Body>
				<Layout.Sidebar>
					{myPartnersList.length > 0 ? (
						<Card
							className="border-t-4 border-[#25a4fc] h-[480px]"
							actions={<Link to="/partners">View all friends &rarr;</Link>}
						>
							<div className="flex items-center justify-between mb-4">
								<h2 className="flex items-center gap-2 text-[22px] font-semibold m-0">
									<Users className="w-5 h-5" /> Friends
								</h2>
							</div>
							<div className="h-full max-h-[390px] overflow-y-auto">
								{myPartnersList && myPartnersList.length > 0 && (
									<ul className="list-none p-0 m-0">
										{myPartnersList.map((partner, index) => (
											<li key={index}>
												<button
													type="button"
													className="flex items-center gap-3 w-full text-left px-2 py-1.5 hover:bg-gray-50 rounded transition-colors cursor-pointer"
													onClick={() => openPartner(partner.partner?._id, partner.partner)}
												>
													<Avatar className="h-10 w-10 flex-shrink-0">
														<AvatarImage src={partner.partner.image} />
														<AvatarFallback />
													</Avatar>
													<div className="flex-1 min-w-0">
														{partner.isAccepted ? (
															<>
																<p className="m-0 text-sm font-medium truncate">{partner.partner.name}</p>
																<p className="m-0 text-xs text-gray-500 truncate">{partner.partner.company}</p>
															</>
														) : (
															<>
																<p className="m-0 text-sm font-medium truncate">{partner.partner.name}</p>
																<p className="m-0 text-xs text-gray-500 truncate">Pending</p>
															</>
														)}
													</div>
												</button>
											</li>
										))}
									</ul>
								)}
							</div>
						</Card>
					) : (
						<Card
							className="border-t-4 border-[#25a4fc] h-[480px]"
							actions={<Link to="/partners">Connect with a new partner &rarr;</Link>}
						>
							<div className="flex items-center justify-between mb-4">
								<h2 className="flex items-center gap-2 text-[22px] font-semibold m-0">
									<Users className="w-5 h-5" /> Friends
								</h2>
							</div>
							<div className="flex items-center justify-center h-full">
								<Link to="/partners" className="no-underline">
									<h3 className="text-center text-[22px] font-normal text-[#25a4fc] m-4 p-1.5 rounded-[15px] hover:font-medium cursor-pointer">
										Add Friends. . .
									</h3>
								</Link>
							</div>
						</Card>
					)}
				</Layout.Sidebar>
			</Layout.Page>
			{partnerDialog}
		</Layout>
		</ScreenShell>
	)
}

export default Community_Page
